import { useMemo } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useMultiAirlineCampaignPerformanceQuery } from "../../../hooks/useCampaignPerformanceQuery";
import { useSelectedAirline } from "../../../hooks/useSelectedAirline";
import { useUserAirlineGroupsSuspenseQuery } from "../../../hooks/useUserAirlineGroupsQuery";
import { ChartNoData } from "../ChartNoData";
import { AverageDiscountChart, type AverageDiscountAirlineSeries } from "./AverageDiscountChart";

export type MultiSeriesAverageDiscountLoaderProps = {
  from: Date;
  to: Date;
};

export const MultiSeriesAverageDiscountLoader = ({ from, to }: MultiSeriesAverageDiscountLoaderProps) => {
  const i18n = useI18n();
  const { selectedAirline, selectedAirlineCodes } = useSelectedAirline({ useSuspense: true });
  const { data: userAirlineGroups } = useUserAirlineGroupsSuspenseQuery();

  const airlineMetrics = useMultiAirlineCampaignPerformanceQuery({
    from,
    to,
    metrics: ["AVERAGE_DISCOUNT_PER_BOOKING", "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING"],
    airlineCodes: selectedAirlineCodes,
  });

  const series: AverageDiscountAirlineSeries[] = useMemo(() => {
    const airlineNameByCode = new Map<string, string>(
      userAirlineGroups.flatMap(({ airlines }) => airlines.map(({ code, name }) => [code, name] as const))
    );
    if (selectedAirline) {
      airlineNameByCode.set(selectedAirline.code, selectedAirline.name);
    }

    return airlineMetrics.flatMap(({ airlineCode, metrics }) => {
      const euroMetric = metrics.find((metric) => metric.metadata.metricName === "AVERAGE_DISCOUNT_PER_BOOKING");
      const percentageMetric = metrics.find(
        (metric) => metric.metadata.metricName === "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING"
      );

      return euroMetric && percentageMetric
        ? [
            {
              airlineCode,
              airlineName: airlineNameByCode.get(airlineCode) ?? airlineCode,
              euroMetric,
              percentageMetric,
            },
          ]
        : [];
    });
  }, [airlineMetrics, userAirlineGroups, selectedAirline]);

  if (series.length === 0) {
    return <ChartNoData titleText={i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_name"))} />;
  }

  return <AverageDiscountChart series={series} multiAirline />;
};
